/**
 * EffectsGovernanceTab - fourth sub-tab of the action modal.
 *
 * Two collapsible panels:
 *   1. Side effects (ordered list with handler picker)
 *   2. UI hints (JSON object fallback)
 */

import { useMemo, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'

import { Label } from '@/shared/components/ui/label'

import { JsonObjectInput } from './json-object-input'
import { SideEffectsEditor } from './side-effects-editor'
import { readForm, setFormField, type ActionFormContext } from './types'

interface EffectsGovernanceTabProps {
  formCtx: ActionFormContext
}

export function EffectsGovernanceTab({ formCtx }: EffectsGovernanceTabProps) {
  const form = readForm(formCtx)

  const hintsJson = useMemo(
    () => JSON.stringify(form.ui_hints ?? {}, null, 2),
    [form.ui_hints],
  )

  return (
    <div className="flex flex-col gap-3">
      <Panel
        title="Side effects"
        subtitle={`${(form.side_effects ?? []).length} configured`}
        defaultOpen
      >
        <SideEffectsEditor
          value={form.side_effects ?? []}
          onChange={(next) => setFormField(formCtx, 'side_effects', next)}
        />
      </Panel>

      <Panel title="UI hints" subtitle="JSON object">
        <div className="flex flex-col gap-1">
          <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
            UI hints (JSON object)
          </Label>
          <JsonObjectInput
            key={hintsJson}
            value={hintsJson}
            onCommit={(next) => setFormField(formCtx, 'ui_hints', next)}
          />
        </div>
      </Panel>
    </div>
  )
}

interface PanelProps {
  title: string
  subtitle?: string
  defaultOpen?: boolean
  children: React.ReactNode
}

function Panel({ title, subtitle, defaultOpen = false, children }: PanelProps) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)]">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-2 bg-[var(--bg-subtle)] px-3 py-2 text-left hover:bg-[var(--bg)]"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className="text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-2)]">
            {title}
          </span>
        </div>
        {subtitle && (
          <span className="text-[10px] text-[var(--ink-3)]">{subtitle}</span>
        )}
      </button>
      {open && <div className="bg-[var(--bg)] p-3">{children}</div>}
    </div>
  )
}
